import { NgClass, NgTemplateOutlet, UpperCasePipe } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FilterService } from "@portal/pages/installations/services/filter.service";
import { NotificationBubbleComponent } from "@portal/pages/notifications/components/notification-bubble/notification-bubble.component";
import { AuthenticationService } from "@portal/services/authentication.service";
import { DomainData, DomainDataService } from "@portal/services/domain-data.service";
import { GlobalStateService } from "@portal/services/global-state.service";
import { MapDetailService } from "@portal/services/map-detail.service";
import { NavigationService } from "@portal/services/navigation.service";
import { PrimeComponentService } from "@portal/services/prime-component.service";
import { SvgIconComponent } from "@portal/shared/components/svg-icon/svg-icon.component";
import { TranslateEnumPipe } from "@portal/shared/pipes/translate-enum.pipe";
import { SubscriptionManager } from "@ramudden/core/utils";
import { RolesApi } from "@ramudden/data-access/resource/roles.api";
import { UserApi } from "@ramudden/data-access/resource/user.api";
import { NavMenuLink } from "@ramudden/models/nav-menu-link";
import { Rights } from "@ramudden/models/rights";
import { AuthorizationInfo, IOrganization, Roles } from "@ramudden/models/user";
import {
    ConfigurationService,
    ConnectivityService,
    DocumentEventService,
    ImpersonationService,
    MapDataService,
    ProgressService,
    ResizeService,
    UploadFileService,
    ValidationService,
    WebsiteService,
} from "@ramudden/services";
import {
    ModalService,
    SelectControlComponent,
    SelectItemTemplateDirective,
    SelectOption,
    SelectSelectedItemTemplateDirective,
} from "@ramudden/ui";
import { firstValueFrom } from "rxjs";
import { filter } from "rxjs/operators";
import { environment } from "../../../../environments/environment";

@Component({
    selector: "app-page-header",
    imports: [
        SelectControlComponent,
        SelectItemTemplateDirective,
        SelectSelectedItemTemplateDirective,
        RouterLink,
        TranslateModule,
        NotificationBubbleComponent,
        SvgIconComponent,
        TranslateEnumPipe,
        NgTemplateOutlet,
        NgClass,
        RouterLinkActive,
        UpperCasePipe,
        FormsModule,
    ],
    templateUrl: "./page-header.component.html",
    styleUrl: "./page-header.component.scss",
})
export class PageHeaderComponent implements OnInit, OnDestroy {
    @ViewChild("nav", { static: true }) navElementRef: ElementRef<HTMLDivElement>;
    impersonationOrganizations: SelectOption[] = [];
    roles: SelectOption[] = [];
    languages: SelectOption[] = [];
    isAuthenticated: boolean | null = null;
    isAuthorized: boolean;
    availableNavLinks: NavMenuLink[] = [];
    authorizationInfo: AuthorizationInfo;
    rights: Rights;
    private navbar: HTMLDivElement;
    private navDropdown: HTMLLIElement;
    private organizationLinkIndex: number;

    private subscriptionManager = new SubscriptionManager();
    private readonly mapDataKey: string;
    private languageChangeId = 0;

    readonly impersonationService = inject(ImpersonationService);
    readonly translate = inject(TranslateService);
    readonly authenticationService = inject(AuthenticationService);
    readonly mapDetailService = inject(MapDetailService);
    readonly router = inject(Router);
    readonly connectivityService = inject(ConnectivityService);
    protected readonly websiteService = inject(WebsiteService);
    private readonly validationService = inject(ValidationService);
    private readonly navigationService = inject(NavigationService);
    private readonly uploadFileService = inject(UploadFileService);
    private readonly progressService = inject(ProgressService);
    private readonly primeComponentService = inject(PrimeComponentService);
    private readonly mapDataService = inject(MapDataService);
    private readonly domainDataService = inject(DomainDataService);
    private readonly documentEventService = inject(DocumentEventService);
    private readonly resizeService = inject(ResizeService);
    private readonly globalStateService = inject(GlobalStateService);
    private readonly userApi = inject(UserApi);
    private readonly rolesApi = inject(RolesApi);
    private readonly modalService = inject(ModalService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly filterService = inject(FilterService);

    constructor() {
        this.mapDataKey = this.mapDataService.createKey();
    }

    @ViewChild("navbar", { static: true }) set setNavbar(navbar: ElementRef<HTMLDivElement>) {
        this.navbar = navbar ? navbar.nativeElement : null;
    }

    @ViewChild("navdropdown", { static: false }) set setNavDropdown(navDropdown: ElementRef<HTMLLIElement>) {
        this.navDropdown = navDropdown ? navDropdown.nativeElement : null;
    }

    get showMockProgress(): boolean {
        return this.authorizationInfo?.isActualDomainAdministrator && !environment.production;
    }

    private readonly configurationService = inject(ConfigurationService);

    protected get envLabel(): string | null {
        return this.configurationService.configuration?.title || null;
    }

    get isMobile(): boolean {
        return this.resizeService.isMobile;
    }

    get isDesktop(): boolean {
        return this.resizeService.isDesktop;
    }

    get organization(): IOrganization {
        return this.globalStateService.getDefaultOrganization();
    }

    get showImpersonationDropdown(): boolean {
        return (
            this.impersonationService.isImpersonating() ||
            this.globalStateService.getAuthorizationInfo()?.isDomainAdministrator
        );
    }

    get showRoleImpersonationDropdown(): boolean {
        return this.globalStateService.hasAccessToRoleImpersonation();
    }

    get currentLanguage(): string {
        return this.translate.currentLang;
    }

    filterImpersonateRoles() {
        let allowedRoles = this.globalStateService.getRoles(this.authorizationInfo?.user);
        if (this.impersonationService.isImpersonating()) {
            allowedRoles = this.globalStateService.getRoles(this.authorizationInfo?.user, true);
        }

        this.roles = [];
        const roles = this.domainDataService.get(DomainData.Roles, { orderBy: (x) => x.label });
        (roles ?? []).forEach((role) => {
            if (allowedRoles.contains(role.value as Roles)) {
                this.roles.push(role);
            }
        });
    }

    ngOnInit() {
        this.filterImpersonateRoles();

        const loadOrganizations = async () => {
            // Use the accessible organizations endpoint to get all orgs user can impersonate
            // This bypasses impersonation filter so users can switch between orgs
            const accessibleOrganizations = await this.mapDataService.loadAccessibleOrganizations();
            this.impersonationOrganizations = this.primeComponentService.createDropdownList(
                accessibleOrganizations,
                (x) => x,
                (x) => x.name,
                false,
            );
            this.cdr.detectChanges();
        };

        this.authorizationInfo = this.globalStateService.getAuthorizationInfo();
        if (this.authorizationInfo) {
            loadOrganizations();
            this.updateOrganizationRouterLink();
            this.filterImpersonateRoles();
        } else {
            this.impersonationService.clear();
        }

        this.rights = this.globalStateService.getCurrentRights();
        if (this.rights) {
            this.updateNavLinks(this.router.url);
        }

        this.languages = this.domainDataService.get(DomainData.Language);

        const onNavigationEndSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.updateNavLinks(event.urlAfterRedirects);
            });

        this.subscriptionManager.add("onNavigationEndSubscription", onNavigationEndSubscription);
        this.isAuthenticated = this.globalStateService.getIsAuthenticated();

        if (this.globalStateService.getIsAuthorized() !== null) {
            this.isAuthorized = this.globalStateService.getIsAuthorized();
        }

        this.updateNavLinks(this.router.url);
    }

    ngOnDestroy() {
        this.subscriptionManager.clear();
        this.mapDataService.unsubscribe(this.mapDataKey);
    }

    private async updateNavLinks(url: string, forcefullyRedirectWhenDeemedRequired = false) {
        const setInitialIfRequired = async () => {
            if (!this.rights || this.rights.backendRights.length == 0) return;

            this.availableNavLinks = this.authenticationService.getNavigationLinks(this.rights);

            // find position of organization link
            this.organizationLinkIndex = this.availableNavLinks.findIndex(
                (x) => x.screenName === "OrganizationsScreen",
            );
            this.updateOrganizationRouterLink();

            if (forcefullyRedirectWhenDeemedRequired) {
                const curNavLink = this.availableNavLinks.filter((x) => url.contains(x.route));
                if (!curNavLink.length) {
                    const firstPossibleRoute = this.availableNavLinks.takeFirstOrDefault();

                    if (firstPossibleRoute) {
                        // Currently in window where we lost rights to, go to first
                        await this.router.navigate([firstPossibleRoute.route]);
                    }
                }
            }
        };

        // about and documentation pages are a special cases, they're placed in the login dropdown menu or they are not present as option at all (documentation)
        if (!url || url === "/about" || url.contains("/documentation")) {
            await setInitialIfRequired();
            return;
        }

        await setInitialIfRequired();
    }

    toggleNavbar() {
        this.navbar.classList.toggle("show");
    }

    toggleDropdown() {
        if (!this.navDropdown) {
            return;
        }

        const documentServiceKey = "closeNavDropdown";

        if (this.navDropdown.classList.contains("open")) {
            this.navDropdown.classList.remove("open");
            this.documentEventService.removeOnClick(documentServiceKey);
        } else {
            this.navDropdown.classList.add("open");
            this.documentEventService.addOnClick(documentServiceKey, this.navDropdown, () => this.toggleDropdown());
        }
    }

    closeMenus() {
        if (this.navbar && this.navbar.classList.contains("show")) this.toggleNavbar();
        if (this.navDropdown && this.navDropdown.classList.contains("open")) this.toggleDropdown();
    }

    setLanguage(language: string) {
        // Guard against rapid successive language switches: if the user picks another
        // language while this reload is in flight, a newer change takes over and we must
        // not apply this (now stale) one, otherwise the app could end up on the language
        // whose response happened to arrive last rather than the one last selected.
        const changeId = ++this.languageChangeId;

        this.domainDataService.reload(false, language).then((_domainData) => {
            if (changeId !== this.languageChangeId) return;

            this.translate.use(language);

            // update user api language
            if (this.authorizationInfo?.user) {
                this.userApi.updateSelfLanguage$(language).subscribe();
            }
        });
    }

    mockProgress() {
        this.uploadFileService.mock();
        this.progressService.mock();
    }

    async setImpersonation(organization: IOrganization) {
        const onOk = async () => {
            this.validationService.setContext(null);
            // Clear persisted filter state to prevent stale project/organization data
            // from being used after switching to a different organization context
            this.filterService.clearPersistedFilterState();
            this.impersonationService.organization = organization;
            this.updateOrganizationRouterLink();
            await this.navigationService.toHome();
            window.location.reload();
        };

        this.modalService.confirm("impersonation.changeOrganization", onOk, () => {}, "info", false);
    }

    async setRoleImpersonation(role: Roles) {
        const onOk = async () => {
            this.validationService.setContext(null);
            this.impersonationService.role = role;

            if (role) {
                this.rights = new Rights(await firstValueFrom(this.rolesApi.getRights$(role)));
            } else {
                this.rights = this.globalStateService.getCurrentRights();
            }
            await this.updateNavLinks(this.router.url, true);
            window.location.reload();
        };

        this.modalService.confirm("impersonation.changeRole", onOk, () => {}, "info", false);
    }

    login() {
        this.authenticationService.login();
    }

    logout() {
        this.authenticationService.signoutSilently();
    }

    isActive(link: NavMenuLink) {
        if (this.router.url.contains("organizations") && link.route === "/map/organizations") {
            return true;
        } else {
            return (
                this.mapDetailService.currentMapDetail === link.mapDetail ||
                this.router.isActive(link.route, {
                    paths: "subset",
                    queryParams: "ignored",
                    fragment: "ignored",
                    matrixParams: "ignored",
                })
            );
        }
    }

    private updateOrganizationRouterLink() {
        // Only update the organization link if it exists
        if (this.organizationLinkIndex > -1) {
            if (this.globalStateService.hasMultipleOrganizations()) {
                this.setOrganizationLinkForMultipleOrganizations();
                return;
            }

            this.setOrganizationLinkForSingleOrganization();
        }
    }

    private setOrganizationLinkForMultipleOrganizations() {
        this.availableNavLinks[this.organizationLinkIndex].route = "/administration";
        this.availableNavLinks[this.organizationLinkIndex].title = "organizations.title";
    }

    private setOrganizationLinkForSingleOrganization() {
        const organization = this.globalStateService.getDefaultOrganization();
        if (!organization) {
            this.availableNavLinks[this.organizationLinkIndex].route = "/";
            return;
        }

        this.availableNavLinks[this.organizationLinkIndex].route =
            `administration/organization/${organization.id}/details`;
        this.availableNavLinks[this.organizationLinkIndex].title = "general.organization";

        if (window.location.href.endsWith("/administration")) {
            this.navigationService.toOrganizations();
        }
    }
}
